import { collection, CollectionReference, DocumentData, Firestore, getDocs, onSnapshot, query, Query, QuerySnapshot, where } from 'firebase/firestore';
import { typeUniqueId } from '../../../../../../dataObjects/types';
import { enumFirestoreCollectionNames, enumFirestoreDataRepositoryDataType } from '../enums';
import { IFirestoreUserRepository } from '../FirestoreUserRepository';
import { FirestoreDataRepositoryFactory } from '../FirestoreDataRepositoryFactory';
import { IFirestoreBaseRepository } from '../FirestoreBaseRepository';
import { IUser, IUserAsJson } from '../../../../../../dataObjects/models/users/User';
import { enumObjectPersistenceState } from '../../../../../../dataObjects/enums';
import { IFirestoreCategoryRepository_Ext } from '.';
import { Category, ICategory, ICategoryAsJson } from '../../../../../../dataObjects/models/categories/Category';
import { JsonConverter } from '../../../../../../dataObjects/utilities/JsonConverter';
import { enumMdbErrorType } from '../../../../../../errorObjects/enums';
import { MdbError } from '../../../../../../errorObjects/MdbError';

/** 
 * @class FirestoreCategoryRepository_Ext Provides extended functionality beyond the FirestoreCategoryRepository class
 * *** IMPORTANT NOTE: FirestoreCategoryRepository_Ext differs from other types of repositories. Instead of working with individual instances
 *   for creating, update, deleting, the FirestorePersistenceMetadataRepository is focused on other features.
 *   Therefore, 
 *     1) FirestoreCategoryRepository_Ext does not extend FirestoreBaseRepository<> as other repository types do; and
 *     2) The FirestoreDataRepositoryFactory cannot be used to instantiate instances of FirestoreCategoryRepository_Ext.
 */
export class FirestoreCategoryRepository_Ext implements IFirestoreCategoryRepository_Ext {

  /**
   * @method Constructor method
   * @param {Firestore} firestoreDb A Firestore DB Context
   */
  constructor(
    firestoreDb: Firestore
  ) {
    this._firestoreDb = firestoreDb;
  }

  /*-----------------------------------------------*/
  /**
   * @property {Firestore} _firestoreDb A reference to the configured Firestore DB
   */
  private _firestoreDb: Firestore;

  /**
   * @method firestoreDb Getter method for _firestoreDb
   */
  get firestoreDb(): Firestore {
    return this._firestoreDb;
  }

  /**
   * @method firestoreDb Setter method for _firestoreDb
   * @param {Firestore} value The value to be used in setting _firestoreDb
   */
  set firestoreDb(value: Firestore) {
    this._firestoreDb = value;
  }

  /*-----------------------------------------------*/
  /**
   * @method getCategoriesForUser_onSnapshot Makes an onSnapshot() request to firestore for retrieving all Categories from the database associated with 
   *   a given User. A firebase onSnapshot() request sets up a subscription to firebase to dynamically update the results.
   * @param {typeUniqueId} userId The unique Id of the User of the objects to retrieve.
   * @param {(snapshot: QuerySnapshot<DocumentData>) => void} callback A callback function that is able to receive 
   *   a snapshot of data from firestore.
   * @returns {Promise<() => void>} A callback for unsubscribing from the firebase onSnapshot request.
   */
  getCategoriesForUser_onSnapshot(userId: typeUniqueId, callback: (snapshot: QuerySnapshot<DocumentData>) => void): Promise<() => void> {
    return new Promise<() => void>(async (resolve, reject) => {
      try {
        // Declare an 'unsubscribeCallback' variable that will hold the unsubscribe callback from a firestore onSnapshot() request and will be returned
        // from this method.  
        // We initialize it to a function that does nothing.
        let unsubscribeCallback: () => void = () => { };
        // console.log(`FirestoreCategoryRepository:getCategorysForUser_onSnapshot before initiating snapshot, unsubscribeCallback: ${unsubscribeCallback}`);

        // get a User repository from the repository factory
        const firestoreUserRepository: IFirestoreUserRepository =
          FirestoreDataRepositoryFactory.CreateDataRepository(enumFirestoreDataRepositoryDataType.User) as
          IFirestoreBaseRepository<IUser, IUserAsJson>;

        const user: IUser | undefined = await firestoreUserRepository.get(userId);

        if (user === undefined) {
          throw new MdbError(`Unable to find a User with the given Id: ${userId}`, enumMdbErrorType.DataNotFound);
        }

        // get the reference to the Firestore DB instance
        const firestoreDb: Firestore = this._firestoreDb;

        // get a reference to the Category collection
        const categoriesCollection: CollectionReference<DocumentData> = collection(firestoreDb, enumFirestoreCollectionNames.CategoryCollection);

        // prepare a query that will get all Category documents where the parentId matches the userId and
        // is in either the 'New' or 'Modified' state
        const documentQuery: Query<DocumentData> =
          query(categoriesCollection,
            where("parentId", "==", userId),
            where("objectState", "in", [enumObjectPersistenceState.New, enumObjectPersistenceState.Modified])
          );

        // with the query object, call onSnapshot() to subscribe to dynamic updates, passing the callback function and capturing the firestore
        // 'unsubscribe' callback function
        unsubscribeCallback = await onSnapshot(documentQuery, callback);
        // await this.sleep(2000);
        // console.log(`FirestoreCategoryRepository:getCategorysForUser_onSnapshot after initiating snapshot, unsubscribeCallback: ${unsubscribeCallback}`);

        // return the 'unsubscribeCallback' with the Promise, via the 'resolve'
        resolve(unsubscribeCallback);
      }
      catch (error: any) {
        reject(error);
      }
    });
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @method getAllCategoriesForOwnerIdAndChannelId Retrieves all Category records that belong to an Owner (ownerId) and have a 
   *                                                specific Channel (channelId) as a child.
   * @param {typeUniqueId} ownerId Id of the Owner.
   * @param {typeUniqueId} channelId Id of the Channel.
   * @returns {Promise<Array<ICategory> | undefined>} A Promise (to provide asynchrounous capability) with an array of object instances (if
   * one or more objects were found to be associated with the given ownerId & channelId) or 'undefined' (if no objects were found to
   * be associated with the given ownerId & channelId).
   */
  getAllCategoriesForOwnerIdAndChannelId(ownerId: typeUniqueId, channelId: typeUniqueId): Promise<Array<ICategory> | undefined> {
    return new Promise<Array<ICategory> | undefined>(async (resolve, reject) => {
      try {
        // array of objects to return
        let categories: Array<ICategory> | undefined = undefined;

        // get the reference to the Firestore DB instance
        const firestoreDb: Firestore = this._firestoreDb;

        // get a reference to the Category collection
        const categoriesCollection: CollectionReference<DocumentData> = collection(firestoreDb, enumFirestoreCollectionNames.CategoryCollection);

        // prepare a query that will get all Category documents with the given ownerId -and-  appears in the Category's memberUsers array -and- the Category
        // is in either the 'New' or 'Modified' state
        // const query: Query<DocumentData> = categoriesCollection
        //   .where("ownerId", "==", ownerId)
        //   .where("children", "array-contains", channelId)
        //   .where("objectState", "in", [enumObjectPersistenceState.New, enumObjectPersistenceState.Modified]);
        const documentQuery: Query<DocumentData> =
          query(categoriesCollection,
            where("ownerId", "==", ownerId),
            where("children", "array-contains", channelId),
            where("objectState", "in", [enumObjectPersistenceState.New, enumObjectPersistenceState.Modified])
          );

        // next, get a snapshot of the document references resulting from executing the query
        const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(documentQuery);

        // now, from the snapshot, get the data for each doc and add the data into a JSON objects array
        let categoriesAsJson: Array<ICategoryAsJson> = [];
        querySnapshot.forEach((doc) => {
          categoriesAsJson.push(doc.data() as ICategoryAsJson);
        });

        // next, convert the array of JSON objects for return
        categories = JsonConverter.arrayFromJSONArray(Category, categoriesAsJson);

        resolve(categories);
      } catch (error: any) {
        reject(error);
      }
    });
  }
  /*-----------------------------------------------*/

  /*-----------------------------------------------*/
  /**
   * @method getAllCategoriesForOwnerIdAndChannelId Retrieves all Category records that have a specific Channel (channelId) as a child.
   * @param {typeUniqueId} channelId Id of the Channel.
   * @returns {Promise<Array<ICategory> | undefined>} A Promise (to provide asynchrounous capability) with an array of object instances (if
   * one or more objects were found to be associated with the given ownerId & channelId) or 'undefined' (if no objects were found to
   * be associated with the given ownerId & channelId).
   */
  getAllCategoriesWithChannelAsChild(channelId: typeUniqueId): Promise<Array<ICategory> | undefined> {
    return new Promise<Array<ICategory> | undefined>(async (resolve, reject) => {
      try {
        // array of objects to return
        let categories: Array<ICategory> | undefined = undefined;

        // get the reference to the Firestore DB instance
        const firestoreDb: Firestore = this._firestoreDb;

        // get a reference to the Category collection
        const categoriesCollection: CollectionReference<DocumentData> = collection(firestoreDb, enumFirestoreCollectionNames.CategoryCollection);

        // prepare a query that will get all Category documents with the given ownerId -and-  appears in the Category's memberUsers array -and- the Category
        // is in either the 'New' or 'Modified' state
        // const query: Query<DocumentData> = categoriesCollection
        //   .where("children", "array-contains", channelId)
        //   .where("objectState", "in", [enumObjectPersistenceState.New, enumObjectPersistenceState.Modified]);
        const documentQuery: Query<DocumentData> =
          query(categoriesCollection,
            where("children", "array-contains", channelId),
            where("objectState", "in", [enumObjectPersistenceState.New, enumObjectPersistenceState.Modified])
          );

        // next, get a snapshot of the document references resulting from executing the query
        const querySnapshot: QuerySnapshot<DocumentData> = await getDocs(documentQuery);

        // now, from the snapshot, get the data for each doc and add the data into a JSON objects array
        let categoriesAsJson: Array<ICategoryAsJson> = [];
        querySnapshot.forEach((doc) => {
          categoriesAsJson.push(doc.data() as ICategoryAsJson);
        });

        // next, convert the array of JSON objects for return
        categories = JsonConverter.arrayFromJSONArray(Category, categoriesAsJson);

        resolve(categories);
      } catch (error: any) {
        reject(error);
      }
    });
  }
  /*-----------------------------------------------*/

}
